<script>
  import SvgUnmute from "$assets/svg/ic_unmute.svg?src";
  import SvgPlay from "$assets/svg/play.svg?src";
  import { togglePostLikeById } from "@/api";
  import SvgWrapper from "@/components/atoms/SvgWrapper.svelte";
  import IntersectionObserver from "@/components/molecules/IntersectionObserver.svelte";
  import StaticInstallBanner from "@/components/molecules/StaticInstallBanner.svelte";
  import VideoOverlay from "@/components/molecules/VideoOverlay.svelte";
  import Comments from "@/components/sections/Comments/index.svelte";
  import constants from "@/constants";
  import { VIDEO_FEED_BLOCK } from "@/constants/constants";
  import trackEvents from "@/events";
  import EVENTS from "@/events/events";
  import landingPageStore from "@/stores/landingPageStore";
  import { shouldMuteVideo } from "@/stores/shouldMuteVideo";
  import { appendWidthParam, isBrowser } from "@/utils";
  import * as css from "@/utils/css";
  import { isMobile, updateCachedObjectWithId } from "@/utils/helpers/utils";
  import throttle from "lodash/throttle.js";
  import { afterUpdate, onDestroy, onMount } from "svelte";
  import InstallPopup from "./InstallPopup.svelte";

  const getCompressedVideoUrl = (videos) => {
    const videosCount = videos.length;
    return videosCount > 1
      ? videos[videosCount - 2].url
      : videosCount === 1
      ? videos[0].url
      : "";
  };

  const { DEFAULT_SRC } = constants;
  export let item,
    toggleLikeCount,
    index = 0,
    isItemInView = false,
    isDragging = false,
    showSideBar = false,
    showDefaultOverlay = true,
    heightClass = "h-[inherit]",
    objectFitClass = "object-contain",
    shouldRunInLoop = true,
    videoPoster = DEFAULT_SRC,
    preload = "metadata",
    visible = null,
    shouldStopPlaying = false,
    videoPausedFunc,
    logVideoItemPlayDuration,
    videoPlayFunc,
    isCampaignVideo = false,
    toggleInstallBanner = () => {};

  let videoElement,
    isVideoPaused = false,
    isBuffering = false,
    isVideoMuted = false,
    prevIsItemInView = false,
    prevShowSideBar = false,
    prevIsVideoMuted = false,
    showComments = false,
    showDownloadPopup = false,
    isHardNudge = false,
    lastStartTimestamp = null,
    totalPlayedTime = 0;
  let videoSrc;
  let videoSrcH264;
  let videoSrcH265;
  let showInstallNudge = false,
    videoDuration,
    playTimeThreshold = 2,
    videoPlayPercent,
    throttledInstallNudge;
  const landingTime = new Date().getTime();
  const videoCompletionThreshold = 0.5;
  const imageAlt = (item.c || "").trim() || "poster";
  $: {
    const {
      h265Videos = [],
      compressedVideoUrl = "",
      v: videoUrl = "",
      bandwidthParsedVideos: h264Videos = [],
    } = item;
    videoSrc = compressedVideoUrl || videoUrl;
    videoSrcH264 = getCompressedVideoUrl(h264Videos);
  }

  $: isFirstCampaignLoad = !!item;

  $: {
    const shouldShowInstallNudge = !$landingPageStore.isInstallNudgeShown;

    if (isMobile() && shouldShowInstallNudge) {
      if (index === 0) {
        videoElement?.addEventListener("timeupdate", throttledInstallNudge);
      }
      if (index === 1) {
        videoElement?.removeEventListener("timeupdate", throttledInstallNudge);
      }
    } else if (isBrowser()) {
      localStorage.removeItem(VIDEO_FEED_BLOCK.UNIQUE_VIDEO_COUNTS);
    }
  }

  $: muted = $shouldMuteVideo || isVideoMuted;

  const playVideo = (fireEventOnSuccess, handleCatch) => {
    if (videoElement && videoElement.paused) {
      const promise = videoElement.play();
      if (promise) {
        promise
          .then(() => {
            if (fireEventOnSuccess) {
              videoPlayStartEvent();
            }
            lastStartTimestamp = Date.now();
            if (videoPlayFunc) videoPlayFunc();
          })
          .catch(() => {
            if (handleCatch) {
              if (!isVideoMuted) {
                isVideoMuted = true;
              } else {
                pauseVideo();
              }
            }
          });
      }
    }
  };

  const pauseVideo = () => {
    if (videoElement && !videoElement.paused) {
      videoElement.pause();
      if (lastStartTimestamp) {
        totalPlayedTime += Date.now() - lastStartTimestamp;
        lastStartTimestamp = null;
      }
    }
  };

  const toggleVideoPlaying = () => {
    if (videoElement && !isDragging) {
      if (isCampaignVideo && muted) {
        shouldMuteVideo.set(false);
      }
      if (videoElement.paused) {
        isVideoMuted = item.isMuted;
        playVideo();
      } else if (videoElement.currentTime > 0) {
        pauseVideo();
      }
    }
  };

  const onBuffering = () => {
    isBuffering = true;
  };
  const onPlaying = () => {
    isBuffering = false;
  };

  const videoPlayStartEvent = () => {
    if (item.i) {
      trackEvents(EVENTS.VIDEO_PLAY_START, {
        postId: item.i,
        p: item.i,
        videoUrl: videoSrc,
        referrer: `trendingFeed${index > 0 ? "Suggested" : ""}`,
      });
    }
  };

  const videoPlayEvent = () => {
    const videoDuration = videoElement?.duration;
    if (!item.i || !videoDuration) return;

    const videoDurationInMs = videoDuration * 1000;
    const timePlayed = lastStartTimestamp
      ? totalPlayedTime + (Date.now() - lastStartTimestamp)
      : totalPlayedTime;
    trackEvents(EVENTS.VIDEO_PLAY, {
      postId: item.i,
      p: item.i,
      videoUrl: videoSrc,
      referrer: `trendingFeed${index > 0 ? "Suggested" : ""}`,
      timeSpent: timePlayed,
      repeatCount: Math.floor(timePlayed / videoDurationInMs),
      percentage: ((timePlayed % videoDurationInMs) / videoDurationInMs) * 100,
      duration: videoDuration,
    });

    totalPlayedTime = 0;
    lastStartTimestamp = null;
  };

  const handleUnmuteClick = (e) => {
    if (e) e.stopPropagation();
    shouldMuteVideo.set(false);
    isVideoMuted = item.isMuted;
  };

  const handleShowComment = () => {
    pauseVideo();
    showComments = true;
  };

  const handleCloseComment = () => {
    showComments = false;
    playVideo();
  };

  const onPopupClose = () => {
    showDownloadPopup = false;
    playVideo(true, true);
  };

  afterUpdate(() => {
    if (prevIsItemInView !== isItemInView) {
      if (isItemInView) {
        isVideoMuted = item.isMuted;
        if (item.hardBlock || item.softBlock) {
          showDownloadPopup = true;
          if (item.hardBlock) isHardNudge = true;
        } else {
          playVideo(true, true);
        }

        updateCachedObjectWithId(item.i);
      } else {
        pauseVideo();
        videoPlayEvent();
      }
    } else if (prevShowSideBar !== showSideBar && isItemInView) {
      if (showSideBar) {
        pauseVideo();
      } else {
        playVideo();
      }
    } else if (prevIsVideoMuted !== isVideoMuted && isItemInView) {
      if (isVideoMuted) {
        playVideo(true, true);
      }
    }

    if (isCampaignVideo && isFirstCampaignLoad && videoElement) {
      isVideoPaused = true;
      videoElement.load();
      isFirstCampaignLoad = false;
    }

    if (visible == "HIDDEN" || shouldStopPlaying) {
      isVideoPaused = true;
    }

    prevIsItemInView = isItemInView;
    prevShowSideBar = showSideBar;
    prevIsVideoMuted = isVideoMuted;
  });

  const onNudgeClose = () => {
    toggleInstallBanner(true);
    showInstallNudge = false;
  };

  const installNudge = () => {
    videoDuration = videoElement?.duration;
    videoPlayPercent = videoDuration * videoCompletionThreshold;
    playTimeThreshold =
      videoPlayPercent > playTimeThreshold
        ? playTimeThreshold
        : videoPlayPercent;
    const currentTime = videoElement?.currentTime;
    if (currentTime > playTimeThreshold) {
      showInstallNudge = true;
      toggleInstallBanner(false);
      landingPageStore.updateState({
        isInstallNudgeShown: true,
      });
      videoElement?.removeEventListener("timeupdate", throttledInstallNudge);
    }
  };

  throttledInstallNudge = throttle(installNudge, 1000);

  onMount(async () => {
    if (isItemInView) {
      updateCachedObjectWithId(item.i);
    }
    if (item.isMuted) videoElement.muted = true;
    if (videoElement) videoElement.addEventListener("waiting", onBuffering);
    if (videoElement) videoElement.addEventListener("playing", onPlaying);

    return () => {
      if (logVideoItemPlayDuration) {
        logVideoItemPlayDuration(landingTime, item.n, videoSrc);
      }
    };
  });

  onDestroy(() => {
    if (isItemInView) {
      videoPlayEvent();
    }
    if (videoElement) videoElement.removeEventListener("waiting", onBuffering);
    if (videoElement) videoElement.removeEventListener("playing", onPlaying);
  });
  const likeCountHandler = async (item, index = 0, action) => {
    try {
      const data = await togglePostLikeById(window.fetch, item, action);
      if (data.error) {
        toggleLikeCount(!action, index);
      }
    } catch (err) {
      toggleLikeCount(!action, index);
      console.log(err);
    }
  };
  const toggleLikeButton = (index) => {
    const action = +item.pl ? 0 : 1;
    toggleLikeCount(action, index);
    likeCountHandler(item, index, action);
  };
</script>

<svelte:head>
  {#if !index}
    <link rel="preload" as="image" href={appendWidthParam(item.b, "240")} />
  {/if}
</svelte:head>

{#if item}
  {#if showInstallNudge && index === 0}
    <InstallPopup
      isHardNudge={false}
      showPopup={showInstallNudge}
      onPopupClose={onNudgeClose}
      position={index + 1}
      {isItemInView}
    />
  {/if}

  <div
    class={`relative w-[inherit] ${heightClass} cursor-pointer`}
    data-testid="video-item"
    on:mouseup={toggleVideoPlaying}
  >
    {#if item.b}
      <img
        src={appendWidthParam(item.b, "240")}
        class={`${css.fullCover} mdUp:block ${objectFitClass} w-full h-full mdUp:object-cover`}
        alt={imageAlt}
        width="100%"
        height="100%"
        fetchpriority="high"
      />
    {/if}

    {#if videoSrc}
      <video
        bind:this={videoElement}
        bind:paused={isVideoPaused}
        class={`${css.fullCover} mdUp:block w-full h-full object-cover`}
        autoplay={false}
        loop={shouldRunInLoop}
        playsinline={true}
        disableRemotePlayback
        {preload}
        {muted}
        poster={videoPoster}
      >
        {#if videoSrcH265}
          <source type="video/mp4" src={videoSrcH265} />
        {/if}
        {#if videoSrcH264}
          <source type="video/mp4" src={videoSrcH264} />
        {/if}
        <source type="video/mp4" src={videoSrc} />
      </video>
    {/if}

    {#if isVideoPaused && videoSrc}
      <div class={`${css.fullCover} bg-overlay`} data-testid="svg-play">
        <SvgWrapper
          className={`${css.allCentered} h-5xl w-5xl flex items-start justify-between`}
          svg={SvgPlay}
        />
      </div>
    {/if}

    {#if showDefaultOverlay}
      <VideoOverlay
        {item}
        {videoSrc}
        {handleShowComment}
        {toggleLikeButton}
        {index}
        {isVideoPaused}
        {toggleInstallBanner}
        {playVideo}
      />

      {#if muted && !isVideoPaused && !item.isMuted}
        <div class={`${css.allCentered} z-[1]`}>
          <div
            data-cy="unmute-button"
            on:mousedown|stopPropagation
            on:mouseup={handleUnmuteClick}
            class={` h-2xl w-2xl bg-white ${css.allCenteredChild} rounded-[50%] scale`}
          >
            <SvgWrapper className="h-xl w-xl" svg={SvgUnmute} />
          </div>
        </div>
      {/if}
    {/if}

    {#if showComments}
      <Comments {item} {handleCloseComment} />
    {/if}

    {#if isBuffering && !isDragging && !isVideoPaused}
      <div class={`${css.bottomAlign} z-[1] top-auto h-3xs`}>
        <div class="indeterminate" />
      </div>
    {/if}

    {#if showDownloadPopup}
      <InstallPopup
        {isHardNudge}
        showPopup={showDownloadPopup}
        {onPopupClose}
        position={index + 1}
        {isItemInView}
      />
    {/if}
  </div>
{:else}
  <IntersectionObserver threshold={0.6} let:visible>
    <StaticInstallBanner {toggleInstallBanner} {visible} />
  </IntersectionObserver>
{/if}
